import { Box, Button } from "@mui/material";
import { Popper } from "@mui/material";
import React, { useCallback, useState } from "react";
import {
  AutoSizer,
  Column,
  defaultTableRowRenderer,
  SortDirectionType,
  Table,
  TableCellRenderer,
  TableHeaderProps,
} from "react-virtualized";
import { FilterIndicator } from "./FilterIndicator";
import SortIndicator from "./SortIndicator";
import { Handle, Td, Th } from "./styled";
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from "react-sortable-hoc";
import ReactDOM from "react-dom";

// Connect react-virtualized and react-sortable-hoc
const SortableTable = SortableContainer(Table, {
  withRef: true,
});
const DraggableElement = SortableElement(({ children }: any) => children);
const DraggableHandle = SortableHandle(({ children }: any) => children);

type ColumnData = {
  dataKey: string;
  label: string;
  numeric?: boolean;
  width: number;
  render?: (value: any, row: any, dataKey: string) => any;
  flexGrow?: number;
  sorter?: any;
  filterDropdown?: () => React.ReactNode;
};
type Row = {
  index: number;
};

type VirtualizedTableProps = {
  columns: ColumnData[];
  headerHeight?: number;
  rowHeight?: number;
  sortBy?: string;
  sortDirection?: SortDirectionType;
  onRowClick?: () => void;
  rowCount: number;
  rowGetter: (row: Row) => any;
  onHeaderCellClick?: (dataKey: string) => void;
  cellDataGetter?: ({
    columnData,
    dataKey,
    rowData,
  }: {
    columnData: any;
    dataKey: string;
    rowData: any;
  }) => any;
  activeFilters?: { [dataKey: string]: string | string[] };
  data?: any[];
  onRowSort?: (
    rowData: any,
    oldIndex: number,
    newIndex: number
  ) => Promise<void>;
  rowStyle?: (row: Row) => React.CSSProperties;
};

export const VirtualizedTable: React.FC<VirtualizedTableProps> = ({
  columns,
  headerHeight = 48,
  rowHeight = 48,
  cellDataGetter,
  sortBy,
  sortDirection,
  onHeaderCellClick,
  activeFilters,
  data,
  onRowSort,
  ...tableProps
}) => {
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen((previousOpen) => !previousOpen);
  };

  const canBeOpen = open && Boolean(anchorEl);
  const id = canBeOpen ? "transition-popper" : undefined;

  const handleSelectFilter = useCallback(
    (dataKey: string) => setSelectedFilter(dataKey),
    [setSelectedFilter]
  );

  const headerRenderer = ({
    label,
    columnIndex,
    dataKey,
    sortBy,
    sortDirection,
    filterDropdown,
    onHeaderCellClick,
  }: TableHeaderProps & {
    columnIndex: number;
    filterDropdown?: () => React.ReactNode;
    onHeaderCellClick?: (dataKey: string) => void;
  }) => {
    return (
      <Th>
        <span onClick={() => onHeaderCellClick && onHeaderCellClick(dataKey)}>
          {label}
          {sortBy === dataKey && (
            <SortIndicator
              sortDirection={sortDirection ? sortDirection : "ASC"}
            />
          )}
        </span>
        {filterDropdown && (
          <>
            <Button
              aria-describedby={id}
              onClick={(e) => {
                handleClick(e);
                handleSelectFilter(dataKey);
              }}
            >
              <FilterIndicator
                isActive={activeFilters && Boolean(activeFilters[dataKey])}
              />
            </Button>
            <Popper
              modifiers={[
                {
                  name: "flip",
                  enabled: true,
                  options: {
                    altBoundary: true,
                    rootBoundary: "document",
                    padding: 8,
                  },
                },
                {
                  name: "preventOverflow",
                  enabled: true,
                  options: {
                    altAxis: true,
                    altBoundary: true,
                    tether: true,
                    rootBoundary: "document",
                    padding: 8,
                  },
                },
                {
                  name: "arrow",
                  enabled: false,
                  options: {
                    // element: arrowRef,
                  },
                },
              ]}
              id={id}
              open={open}
              anchorEl={anchorEl}
              // @ts-ignore
              onResize={undefined}
              onResizeCapture={undefined}
            >
              {/* <Box sx={{ border: 1, p: 1, bgcolor: "background.paper" }}>
                The content of the Popper.
              </Box> */}
              <Box>{filterDropdown()}</Box>
            </Popper>
          </>
        )}
      </Th>
    );
  };

  const cellRenderer: TableCellRenderer = ({
    cellData,
    columnIndex,
    rowData,
    dataKey,
    columnData,
  }) => {
    return (
      <Td style={{ height: rowHeight }}>
        {columnIndex != null && columns[columnIndex].render
          ? // @ts-ignore
            columns[columnIndex].render(cellData, rowData, dataKey)
          : cellData}
      </Td>
    );
  };

  const sortRows = useCallback(
    ({ newIndex, oldIndex }) => {
      if (newIndex === oldIndex) {
        return;
      }

      if (data && !!onRowSort) {
        const [removed] = data.splice(oldIndex, 1);
        data.splice(newIndex, 0, removed);
        onRowSort(data[newIndex], oldIndex, newIndex);
      }
    },
    [data, onRowSort]
  );

  const isRowSortable = useCallback(
    (index) => {
      return !!onRowSort && !!data && index >= 0; // Header row should not be draggable
    },
    [data, onRowSort]
  );

  const rowRenderer = useCallback(
    (props) => {
      const { key, index, columns, ...rest } = props;
      return isRowSortable(index) ? (
        <DraggableElement key={key} index={index}>
          <div {...rest}>
            <DraggableHandle>
              <Handle />
            </DraggableHandle>
            {columns}
          </div>
        </DraggableElement>
      ) : (
        defaultTableRowRenderer(props)
      );
    },
    [isRowSortable]
  );

  return (
    <AutoSizer>
      {({ height, width }) => (
        <SortableTable
          // Start sortable props
          // @ts-ignore
          getContainer={(wrappedInstance: any) => {
            const grid = wrappedInstance.Grid;
            return ReactDOM.findDOMNode(grid);
          }}
          onSortEnd={sortRows}
          useDragHandle
          rowRenderer={rowRenderer}
          // End sortable props
          height={height}
          width={width}
          rowHeight={rowHeight!}
          gridStyle={{
            direction: "inherit",
          }}
          headerHeight={headerHeight!}
          sortBy={sortBy}
          sortDirection={sortDirection}
          {...tableProps}
        >
          {columns.map(
            ({ dataKey, sorter, filterDropdown, ...other }, index) => {
              return (
                <Column
                  key={dataKey}
                  headerRenderer={(headerProps) =>
                    headerRenderer({
                      ...headerProps,
                      columnIndex: index,
                      dataKey,
                      sortBy,
                      sortDirection,
                      filterDropdown,
                      onHeaderCellClick: sorter && onHeaderCellClick,
                    })
                  }
                  //className={classes.flexContainer}
                  // @ts-ignore
                  cellDataGetter={cellDataGetter}
                  cellRenderer={cellRenderer}
                  dataKey={dataKey}
                  {...other}
                />
              );
            }
          )}
        </SortableTable>
      )}
    </AutoSizer>
  );
};
